import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef, Component, ElementRef, HostBinding, TemplateRef, ViewChild, ViewEncapsulation
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AnalyticsSettingsEvent } from 'analytics-api';
import { TypePositionOfPager, TypeOfPager, TypeOfScroll, TypeOfTheme, TypePositionOfArrow } from 'cms/cms-shared/common-settings-styles/common-settings-styles.models';
import { AnalyticsComponentEvent } from 'idt-analytics-api/models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderStorageService } from 'render/render-storage/render-storage.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { Subject, asyncScheduler, from, fromEvent, interval } from 'rxjs';
import {
  debounceTime, distinctUntilChanged, filter, mergeMap, skipWhile, take, takeUntil, tap, throttleTime
} from 'rxjs/operators';
import { AnimationsRenderService } from 'shared/animations/animations-render/animations-render.service';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import SwiperCore, { SwiperOptions, Pagination, Autoplay, Scrollbar, EffectCube } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { AutoplayOptions, PaginationOptions } from 'swiper/types';

import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';
import { ArrowType } from 'extensions/core/slice-slider/front/settings/slice-slider-settings.model';
import {
  SLICE_SLIDER_DEFAULT_ARROW_TYPE,
  SLICE_SLIDER_DEFAULT_DELAY,
  SLICE_SLIDER_DEFAULT_NUMBER_OF_SLIDES,
  SLICE_SLIDER_DEFAULT_SPACE_BETWEEN,
  SLICE_SLIDER_DEFAULT_CENTERED_SLIDES,
  SLICE_SLIDER_DEFAULT_FX_SLIDE,
  SLICE_SLIDER_DEFAULT_INITIAL_OFFSET,
  SLICE_SLIDER_DEFAULT_LAST_OFFSET,
  SLICE_SLIDER_DEFAULT_SEPARATOR,
  SLICE_SLIDER_DEFAULT_SPEED,
  SLICE_SLIDER_DEFAULT_HIDE_NAVIGATION
} from 'extensions/core/slice-slider/front/slice-slider.definition';

import { SliceSliderEventsService } from './slice-slider-events.service';
import { templateUrl, styleUrls } from './slice-slider-render.component.paths';


SwiperCore.use([Pagination, Autoplay, Scrollbar, EffectCube]);

@Component({
  selector: 'slice-slider-render',
  templateUrl,
  styleUrls,
  encapsulation: ViewEncapsulation.ShadowDom
})
export class SliceSliderRenderComponent extends SliceBaseRenderComponent implements AfterViewInit {

  @ViewChild(SwiperComponent) public swiperComponent?: SwiperComponent;
  @ViewChild('sliceSliderWrapper') public sliceSliderWrapper!: ElementRef<HTMLElement>;

  @HostBinding('style')
  public get stylesProperties(): string | undefined {
    let style = '';
    if (this.slice?.settings?.general?.arrowSize) {
      style += `--arrow-width-desktop: ${this.slice.settings.general.arrowSize.widthDesktop};
      --arrow-height-desktop: ${this.slice.settings.general.arrowSize.heightDesktop};
      --arrow-width-mobile: ${this.slice.settings.general.arrowSize.widthMobile};
      --arrow-height-mobile: ${this.slice.settings.general.arrowSize.heightMobile};`;
    }
    if (this.hasThemeColors) {
      style += `--primary-light-color: ${this.slice?.settings?.styles?.primaryLightColor};
      --secondary-light-color: ${this.slice?.settings?.styles?.secondaryLightColor};
      --primary-dark-color: ${this.slice?.settings?.styles?.primaryDarkColor};
      --secondary-dark-color: ${this.slice?.settings?.styles?.secondaryDarkColor};`;
    }
    return style ?? undefined;
  }

  @HostBinding('style.background-position')
  public get backgroundPosition(): string | undefined {
    if (this.backgroundImageSetting) {
      return this.backgroundImageSetting.position ?? 'top left';
    }
    return undefined;
  }

  @HostBinding('class.pager-style-dot')
  public get isPagerStyleDot(): boolean { return this.pagerStyle === 'dot'; }

  @HostBinding('class.pager-style-box')
  public get isPagerStyleBox(): boolean { return this.pagerStyle === 'box'; }

  @HostBinding('class.pager-style-solid')
  public get isPagerStyleSolid(): boolean { return this.pagerStyle === 'solid'; }

  @HostBinding('class.pager-style-dashed')
  public get isPagerStyleDashed(): boolean { return this.pagerStyle === 'dashed'; }

  @HostBinding('class.pager-style-count')
  public get isPagerStylecount(): boolean { return this.pagerStyle === 'count'; }

  @HostBinding('class.pager-style-scroll')
  public get isPagerStyleScroll(): boolean { return this.pagerStyle === 'scroll'; }

  @HostBinding('class.pager-position-left')
  public get isPagerPositionLeft(): boolean { return this.pagerPosition.includes('left'); }

  @HostBinding('class.pager-position-center')
  public get isPagerPositionCenter(): boolean { return this.pagerPosition.includes('center'); }

  @HostBinding('class.pager-position-right')
  public get isPagerPositionRight(): boolean { return this.pagerPosition.includes('right'); }

  @HostBinding('class.pager-position-outside')
  public get isPagerPositionOutside(): boolean { return this.pagerPosition.includes('outside'); }

  @HostBinding('class.pager-position-inside')
  public get isPagerPositionInside(): boolean { return this.pagerPosition.includes('inside'); }

  @HostBinding('class.pager-position-top')
  public get isPagerPositionTop(): boolean { return this.pagerPosition.includes('top'); }

  @HostBinding('class.swiper-separator')
  public get isSeparator(): boolean { return this.hasSeparator(); }

  @HostBinding('style.--swiper-space-between')
  public get swiperSpaceBetween(): number { return this.slice?.settings?.general?.spaceBetween ?? 0; }

  @HostBinding('class.slice-slider-horizontal')
  public get swiperDirectionHor(): boolean { return this.slice?.settings.general.direction === 'horizontal'; }

  @HostBinding('class.slice-slider-vertical')
  public get swiperDirectionVer(): boolean { return this.slice?.settings.general.direction === 'vertical'; }

  @HostBinding('class.dark-theme')
  public get isDarkTheme() { return this.styleTheme === 'dark-theme'; }

  @HostBinding('class.light-theme')
  public get isLightTheme() { return this.styleTheme === 'light-theme'; }

  @HostBinding('class.swiper-freeMode')
  public get isScrollFree(): boolean { return this.scrollMode === 'free'; }

  @HostBinding('class.cube-effect')
  public get isCubeEffect(): boolean { return this.slice?.settings.general.transitionType === 'cube'; }

  @HostBinding('style.--opacity-navigation')
  public get hideNavigation(): 0 | 1 {
    const hideNavigation = this.isMobile
      ? this.slice?.settings.general.hideNavigationMobile ?? SLICE_SLIDER_DEFAULT_HIDE_NAVIGATION
      : this.slice?.settings.general.hideNavigation ?? SLICE_SLIDER_DEFAULT_HIDE_NAVIGATION;

    return hideNavigation === true ? 0 : 1;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public get pagerStyle(): TypeOfPager {
    const defaultPaginationTypeValue = 'dot';
    if (this.isMobile) {
      return this.slice?.settings.general.paginationTypeMobile ?? defaultPaginationTypeValue;
    }
    return this.slice?.settings.general.paginationType ?? defaultPaginationTypeValue;
  }

  public get pagerPosition(): TypePositionOfPager {
    const defaultPagerPositionValue = 'inside-center';
    if (this.isMobile) {
      return this.slice?.settings.general.pagerPositionMobile ?? defaultPagerPositionValue;
    }
    return this.slice?.settings.general.pagerPosition ?? defaultPagerPositionValue;
  }

  public get hasThemeColors(): boolean {
    return !!this.slice?.settings?.styles?.primaryLightColor
      && !!this.slice?.settings?.styles?.secondaryLightColor
      && !!this.slice?.settings?.styles?.primaryDarkColor
      && !!this.slice?.settings?.styles?.secondaryDarkColor;
  }

  public get arrowType(): ArrowType {
    return this.slice?.settings?.general.arrowType ?? SLICE_SLIDER_DEFAULT_ARROW_TYPE;
  }

  public get arrowIsIcon(): boolean {
    return this.arrowType === 'icon';
  }

  public get arrowIsCustomIcon(): boolean {
    return this.arrowType === 'custom-icon';
  }

  public get arrowIsTitle(): boolean {
    return this.arrowType === 'title';
  }

  public get arrowPosition(): TypePositionOfArrow {
    return this.slice?.settings.general?.arrowPosition ?? 'inside-left-right';
  }

  public get scrollMode(): TypeOfScroll {
    return this.slice?.settings?.general?.scrollMode ?? 'free';
  }

  public get isScrollPager(): boolean {
    return this.scrollMode === 'pager';
  }

  public get styleTheme(): TypeOfTheme {
    const currentChildKey = (this.slidesRange[this.currentIndex] ?? 0) + 1;
    if (this.slice?.children?.[currentChildKey]) {
      const currentSlideId = this.slice?.children[currentChildKey][0];
      const currentSlide = this.components.find(c => c.id === currentSlideId);

      if (currentSlide?.settings?.general?.theme) {
        return currentSlide?.settings?.general?.theme === 'dark' ? 'dark-theme' : 'light-theme';
      }
    }
    return 'light-theme';
  }

  public get autoplayDelay(): number | number[] {
    return this.slice?.settings.general.delay ?? SLICE_SLIDER_DEFAULT_DELAY;
  }

  public get numberOfSlides(): number {
    const desktopSlides = this.slice?.settings.general.numberOfSlides ?? SLICE_SLIDER_DEFAULT_NUMBER_OF_SLIDES;
    if (this.isMobile) {
      return this.slice?.settings.general.numberOfSlidesMobile ?? desktopSlides;
    }
    return desktopSlides;
  }

  public get showSliderHeader(): boolean {
    return (this.slice.settings.general.showSliderTitle
      && this.slice.settings.general.sliderTitle?.length > 0)
      || this.slice.settings.general.arrowPosition === 'header-right';
  }

  public get slideDescription(): string {
    if (this.slice.settings.general.displaySlideDescriptions && this.slidesRange.length > 0) {
      return this.slideDescriptions[this.currentIndex];
    }

    return '';
  }

  public get fxSlide(): number {
    if (this.isMobile) {
      return this.slice?.settings.general.fxSlideMobile ?? SLICE_SLIDER_DEFAULT_FX_SLIDE;
    }
    return this.slice?.settings.general.fxSlide ?? SLICE_SLIDER_DEFAULT_FX_SLIDE;
  }

  public config: SwiperOptions = {};
  public currentIndex: number = 0;
  public isScale: boolean = false;
  public slidesRange: number[] = [];
  private arrowTitles: string[] = [];
  private slideDescriptions: string[] = [];
  private didReset: boolean = false;
  private readonly showNavigation$: Subject<void> = new Subject<void>();

  constructor(
    protected readonly domSanitizer: DomSanitizer,
    protected readonly assetsSharedService: AssetsSharedService,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly idtSharedRenderService: IdtSharedRenderService,
    protected readonly renderConfigService: RenderConfigurationService,
    protected readonly animationsRenderService: AnimationsRenderService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly el: ElementRef,
    protected readonly renderStateService: RenderStateService,
    protected readonly renderStorageService: RenderStorageService,
    protected readonly renderSlugService: RenderSlugService,
    private readonly contextService: ContextService,
    private readonly swiperEventsService: SliceSliderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly renderEventsService: RenderEventsService
  ) {
    super(
      domSanitizer,
      assetsSharedService,
      breakpointObserver,
      idtSharedRenderService,
      renderConfigService,
      animationsRenderService,
      cd,
      el,
      renderStateService,
      renderStorageService,
      renderSlugService
    );
  }

  public scale(statusScale: boolean): void {
    this.isScale = statusScale;
  }

  public ngAfterViewInit(): void {
    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilChanged(),
      filter((currentLang: StoreLanguage) => !!currentLang?.direction),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      this.el.nativeElement.dir = currentLang.direction;
      this.resetSwiper();
    });
    /** Handle mouse navigate class on this encapsuled component */
    this.handleMouseNavigate();
  }

  public resetSwiperAfterLazyImagesLoad(): void {
    if (this.didReset) {
      // Resetting Swiper causes init() to be called, which triggers the reset again, and so infinitely. We prevent it.
      return;
    }

    this.didReset = true;

    if (this.config.freeMode && this.config.direction === 'horizontal') {
      interval(50).pipe(
        skipWhile(() => {
          // 'elementRef' is private in the SwiperComponent class but useful here
          const swiperComponentAsElement = (this.swiperComponent as unknown as TemplateRef<SwiperComponent>).elementRef;
          const images = Array.prototype.slice.call(swiperComponentAsElement.nativeElement.querySelectorAll('.swiper-slide render-slice-slot img'));
          return images.filter(image => !image.complete).length > 0;
        }),
        take(1),
        takeUntil(this.clearSubscriptions$)
      ).subscribe(() => {
        this.resetSwiper();
      });
    }
  }

  public updateState(): void {
    // Enables X&Y Scaling when the 3D effect is enabled
    this.scale(true);

    this.currentIndex = this.swiperComponent!.swiperRef.realIndex;
    // Needed for discrepance wc rendering swiper
    const activeIndex = this.swiperComponent!.swiperRef.activeIndex;

    // Set the right delay for the current slide
    const delay = this.autoplayDelay;
    if (Array.isArray(delay)) {
      if (this.currentIndex === activeIndex) {
        // This only happens in webcomponent and it is necessary to get the actual index in the delay array
        this.currentIndex = (this.currentIndex - 1) % delay.length;
      }
      (this.swiperComponent!.swiperRef.params.autoplay as AutoplayOptions).delay = delay[this.currentIndex] * 1000;
    }
    // Fix current page number update in webcomponent mode
    this.cd.detectChanges();

    this.emitChangeSlideEvent();
    this.logEventForAnalytics();
  }

  public arrowClick(direction: 'prev' | 'next'): void {
    if (direction === 'next') {
      this.swiperComponent?.swiperRef.slideNext();
    } else {
      this.swiperComponent?.swiperRef.slidePrev();
    }
  }

  public goToSlide(slideIndex: number): void {
    this.swiperComponent?.swiperRef.slideToLoop(slideIndex);
  }

  public showNavigationControls(): void {
    if (this.hideNavigation === 0) {
      this.showNavigation$.next();
    }
  }

  public getArrowText(direction: 'prev' | 'next'): string {
    let title = '';
    const numSlides = this.slidesRange.length;
    if (this.arrowType === 'title' && numSlides > 0) {
      let titleIndex: number;
      if (direction === 'prev') {
        titleIndex = this.currentIndex > 0 ? this.currentIndex - 1 : numSlides - 1;
      } else {
        titleIndex = this.currentIndex < numSlides - 1 ? this.currentIndex + 1 : 0;
      }
      title = this.arrowTitles[titleIndex];
    }
    return title;
  }

  public getArrowIcon(direction: 'prev' | 'next'): string | undefined {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const icon = this.slice.settings.general.arrowCustomIcon[`${direction}Arrow`] as string;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(icon, assetsUrl, akamaiOptions);
  }

  public initialOffset(index: number): string {
    if (index === 0) {
      if (this.isMobile) {
        return this.slice?.settings?.general?.initialOffsetMobile ?? SLICE_SLIDER_DEFAULT_INITIAL_OFFSET;
      }
      return this.slice?.settings?.general?.initialOffset ?? SLICE_SLIDER_DEFAULT_INITIAL_OFFSET;
    }
    return SLICE_SLIDER_DEFAULT_INITIAL_OFFSET;
  }

  public lastOffset(index: number): string {
    if (index === this.slidesRange.length - 1) {
      if (this.isMobile) {
        return this.slice?.settings?.general?.lastOffsetMobile ?? SLICE_SLIDER_DEFAULT_LAST_OFFSET;
      }
      return this.slice?.settings?.general?.lastOffset ?? SLICE_SLIDER_DEFAULT_LAST_OFFSET;
    }
    return SLICE_SLIDER_DEFAULT_INITIAL_OFFSET;
  }

  public spaceBetween(): number {
    if (this.isMobile) {
      return this.slice?.settings.general.spaceBetweenMobile ?? SLICE_SLIDER_DEFAULT_SPACE_BETWEEN;
    }
    return this.slice?.settings.general.spaceBetween ?? SLICE_SLIDER_DEFAULT_SPACE_BETWEEN;
  }

  public speed(): number {
    if (this.isMobile) {
      return (this.slice?.settings.general.speedMobile ?? SLICE_SLIDER_DEFAULT_SPEED) * 1000;
    }
    return (this.slice?.settings.general.speed ?? SLICE_SLIDER_DEFAULT_SPEED) * 1000;
  }

  public isCenteredSlides(): boolean {
    if (this.isMobile) {
      return this.slice?.settings.general.centeredSlidesMobile ?? SLICE_SLIDER_DEFAULT_CENTERED_SLIDES;
    }
    return this.slice?.settings.general.centeredSlides ?? SLICE_SLIDER_DEFAULT_CENTERED_SLIDES;
  }

  public hasSeparator(): boolean {
    if (this.isMobile) {
      return this.slice?.settings?.general?.separatorMobile ?? SLICE_SLIDER_DEFAULT_SEPARATOR;
    }
    return this.slice?.settings?.general?.separator ?? SLICE_SLIDER_DEFAULT_SEPARATOR;
  }

  // Changes in the variable used in the ngFor are not refreshed correctly when track by index.
  public trackBySlide(index: number, slide: number): number {
    return slide;
  }

  protected onSliceInit(): void {
    this.initConfig();

    this.showNavigation$.pipe(
      takeUntil(this.clearSubscriptions$),
      tap(() => this.sliceSliderWrapper.nativeElement.classList.add('hover-mobile')),
      debounceTime(3000),
    ).subscribe(() => {
      this.sliceSliderWrapper.nativeElement.classList.remove('hover-mobile');
    });
  }

  protected onMediaChange(breakpointState: BreakpointState): void {
    super.onMediaChange(breakpointState);

    if (this.config.slidesPerView !== 'auto') {
      this.swiperComponent?.updateSwiper({ slidesPerView: this.numberOfSlides });
    }
    this.initSlides();
  }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice?.settings.styles.bgColor,
      marginTop: this.slice?.settings.styles.marginTop,
      marginRight: this.slice?.settings.styles.marginRight,
      marginLeft: this.slice?.settings.styles.marginLeft,
      marginBottom: this.slice?.settings.styles.marginBottom,
      paddingTop: this.slice?.settings.styles.paddingTop,
      paddingRight: this.slice?.settings.styles.paddingRight,
      paddingLeft: this.slice?.settings.styles.paddingLeft,
      paddingBottom: this.slice?.settings.styles.paddingBottom
    });
  }

  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice?.settings.styles.bgColor,
      marginTop: breakpointStyles?.marginTop || defaultBpSettings.marginTop || '',
      marginRight: breakpointStyles?.marginRight || defaultBpSettings.marginRight || '',
      marginLeft: breakpointStyles?.marginLeft || defaultBpSettings.marginLeft || '',
      marginBottom: breakpointStyles?.marginBottom || defaultBpSettings.marginBottom || '',
      paddingTop: breakpointStyles?.paddingTop || defaultBpSettings.paddingTop || '',
      paddingRight: breakpointStyles?.paddingRight || defaultBpSettings.paddingRight || '',
      paddingLeft: breakpointStyles?.paddingLeft || defaultBpSettings.paddingLeft || '',
      paddingBottom: breakpointStyles?.paddingBottom || defaultBpSettings.paddingBottom || ''
    });
  }

  private initSlides(): void {
    this.slidesRange = Object.keys(this.slice.children)
      .filter(childKey => !this.isHiddenSlide(+childKey - 1) && this.hasContent(this.slice.children[childKey][0]))
      .map(childKey => +childKey - 1);

    const titles = this.slice.settings.general.titles as string[];
    this.arrowTitles = titles.filter((title, index) => this.slidesRange.includes(index));

    const slideDescriptionsData = (this.slice.settings.general.slideDescriptions ?? []) as string[];
    this.slideDescriptions = slideDescriptionsData.filter((slideDescription, index) => this.slidesRange.includes(index));
  }

  private initConfig(): void {
    const delay = Array.isArray(this.autoplayDelay) ? this.autoplayDelay[0] : this.autoplayDelay;
    this.config = {
      autoHeight: false,
      effect: this.slice?.settings.general.transitionType,
      direction: this.slice?.settings.general.direction,
      freeMode: this.isScrollFree,
      init: false,
      initialSlide: this.slice?.settings.general.initialSlide,
      loop: !!this.slice?.settings.general.loop,
      navigation: false,
      observer: true,
      // resizeObserver is used by Oysho to calculate the width of the slider when the webcomponent is dynamically inserted.
      resizeObserver: !!this.renderConfigService.config.experimentalFeatures?.resizeObserverInSliceSlider,
      pagination: false,
      slidesPerView: this.numberOfSlides,
      spaceBetween: this.spaceBetween(),
      updateOnImagesReady: true,
      autoplay: this.slice?.settings.general.autoplay
        ? {
          delay: delay * 1000,
          stopOnLastSlide: true,
          pauseOnMouseEnter: this.slice?.settings.general.pauseOnMouseEnter,
          /* The 'disableOnInteraction' option to false prevents the 'pauseOnMouseEnter' from disabling autoplay
          and having to re-enable it when the mouse is not over a slide */
          ...(this.slice?.settings.general.pauseOnMouseEnter && { disableOnInteraction: false })
        }
        : false,
      speed: this.speed(),
      centeredSlides: this.isCenteredSlides()
    };

    if (this.isCubeEffect) {
      this.config.cubeEffect = { shadow: false };
    }

    if (this.config.freeMode) {
      this.config.slidesPerView = 'auto';
    } else if (this.slice?.settings.general.slidesWidthType === 'manual') {
      this.config.slideClass = 'auto-width';
      this.config.slidesPerView = 'auto';
    }

    if (this.slice?.settings.general.pagination && this.isScrollPager) {
      if (this.isPagerStyleScroll) {
        this.config.scrollbar = {
          draggable: true
        };
        this.config.observeParents = true;
      } else if (!this.isPagerStylecount) {
        this.config.pagination = this.getPaginationOptions();

        // Once the pager is created, the description of the slide is moved to its inside.
        this.cd.detectChanges();
        this.addSlideDescriptionInPagination();
      }
    }
  }

  private getPaginationOptions(): PaginationOptions {
    let bulletClass = 'swiper-pagination-bullet';
    if (this.isPagerStyleBox) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-box`;
    } else if (this.isPagerStyleSolid) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-solid`;
    } else if (this.isPagerStyleDashed) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-dashed`;
    }
    return {
      clickable: true,
      renderBullet: (index: number) => {
        return `<button class="${bulletClass}">${this.isPagerStyleBox ? index + 1 : ''}</button>`;
      },
    };
  }

  private addSlideDescriptionInPagination(): void {
    const paginator = (this.swiperComponent!.pagination as PaginationOptions).el as HTMLElement;
    const slideDescription = this.swiperComponent!.swiperRef.el.getElementsByClassName('slide-description')[0];
    if (slideDescription) {
      paginator.append(slideDescription);
    }
  }

  private emitChangeSlideEvent(): void {
    const event = new Event('changeSlide');
    const renderEvent: RenderEvent = {
      event,
      extra: {
        type: 'sliceSliderChangeSlide'
      },
      location: null,
      data: {
        componentId: this.slice.id,
        currentSlide: this.currentIndex + 1,
        currentTheme: this.styleTheme
      }
    };
    this.renderEventsService.events.next(renderEvent);
  }

  private logEventForAnalytics(): void {
    const eventSliderArrowClicked = this.slice?.settings?.analytics?.events.find(
      (ev: AnalyticsSettingsEvent) => ev.name === 'slider-arrow-clicked'
    );

    if (eventSliderArrowClicked === undefined || !this.swiperComponent) {
      return;
    }

    const currentSlide = this.currentIndex + 1;
    const strippedTitle = this.arrowTitles[currentSlide]?.replace(/(<([^>]+)>)/ig, '');
    const analyticsEventData: AnalyticsComponentEvent = {
      carouselSlide: currentSlide.toString(),
      slideTitle: strippedTitle
    };

    const slug = this.renderSlugService.getSlug();
    this.swiperEventsService.setAnalytics(
      slug,
      eventSliderArrowClicked.relatedEvent,
      analyticsEventData,
      this,
      this.clearSubscriptions$
    );
  }

  private setHostStyles(styles: SliceBaseHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

  private resetSwiper(): void {
    this.swiperComponent?.swiperRef?.destroy();
    this.swiperComponent?.initSwiper();
  }

  private isHiddenSlide(slotIndex: number): boolean {
    return this.slots[slotIndex].hidden ?? false;
  }

  private handleMouseNavigate() {
    if (!isDefined(this.sliceSliderWrapper)) { return; }

    this.sliceSliderWrapper.nativeElement?.classList.add('mouse-navigate');
    const events: string[] = ['mousedown', 'keydown'];
    from(events)
      .pipe(
        mergeMap((event) => fromEvent(window, event)),
        throttleTime(500, asyncScheduler, { leading: false, trailing: true }),
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((event) => {
        this.updateNavigation(event);
      });
  }

  private updateNavigation(event: Event) {
    if (event.type === 'mousedown') {
      this.sliceSliderWrapper.nativeElement?.classList.add('mouse-navigate');
    }
    if (event.type === 'keydown') {
      const eventKeyboard = event as KeyboardEvent;
      if (eventKeyboard.key === 'Tab') {
        this.sliceSliderWrapper.nativeElement?.classList.remove('mouse-navigate');
      }
    }
  }

}
